/**
 * S3 Path Utilities
 * 
 * This file contains utilities for working with S3 paths in a standardized way.
 * It provides functions for generating consistent S3 paths for different resource types.
 */

import { config } from '../constants/config';
import { s3HttpsBase, normalizeS3Key } from './s3KeyUtils';
import axios from 'axios';

// Dual-layer cache for signed URLs
const requestCache = new Map(); // Short-term deduplication cache (10 seconds)
const urlCache = new Map();     // Medium-term signed URL cache (20 minutes)

// Cache cleanup intervals
const REQUEST_CACHE_TTL = 10 * 1000;      // 10 seconds
const URL_CACHE_TTL = 20 * 60 * 1000;     // 20 minutes
const CLEANUP_INTERVAL = 5 * 60 * 1000;   // 5 minutes

// Periodic cleanup of expired cache entries
setInterval(() => {
  const now = Date.now();

  // Clean up expired URL cache entries
  for (const [key, entry] of urlCache.entries()) {
    if (now > entry.expires) {
      urlCache.delete(key);
    }
  }

  // Clean up old request cache entries (they should auto-expire, but just in case)
  for (const [key, entry] of requestCache.entries()) {
    if (now > entry.expires) {
      requestCache.delete(key);
    }
  }
}, CLEANUP_INTERVAL);

/**
 * Get cache statistics for debugging
 * @returns {Object} Cache statistics
 */
export const getSignedUrlCacheStats = () => {
  const now = Date.now();

  // Count active entries
  let activeUrlCache = 0;
  let expiredUrlCache = 0;
  for (const [key, entry] of urlCache.entries()) {
    if (now <= entry.expires) {
      activeUrlCache++;
    } else {
      expiredUrlCache++;
    }
  }

  let activeRequestCache = 0;
  let expiredRequestCache = 0;
  for (const [key, entry] of requestCache.entries()) {
    if (now <= entry.expires) {
      activeRequestCache++;
    } else {
      expiredRequestCache++;
    }
  }

  return {
    urlCache: {
      active: activeUrlCache,
      expired: expiredUrlCache,
      total: urlCache.size
    },
    requestCache: {
      active: activeRequestCache,
      expired: expiredRequestCache,
      total: requestCache.size
    },
    settings: {
      requestCacheTtl: REQUEST_CACHE_TTL,
      urlCacheTtl: URL_CACHE_TTL,
      cleanupInterval: CLEANUP_INTERVAL
    }
  };
};

/**
 * Clear all signed URL caches (useful for testing or manual cache invalidation)
 */
export const clearSignedUrlCaches = () => {
  requestCache.clear();
  urlCache.clear();
  console.log('[SIGNED_URL_CACHE] All caches cleared');
};

/**
 * Generate a standardized S3 path for a resource
 * @param {string} resourceType - The type of resource (e.g., 'adminProfileImage', 'voiceActorProfileImage')
 * @param {Object} params - The parameters for the path
 * @returns {string} The generated S3 path
 */
export const generateS3Path = (resourceType, params) => {
  // Validate required parameters
  if (!resourceType) {
    throw new Error('Resource type is required');
  }

  if (!params) {
    throw new Error('Parameters are required');
  }

  // Validate userId
  if (!params.userId) {
    throw new Error('userId is required');
  }

  // Generate path based on resource type
  switch (resourceType) {
    case 'adminProfileImage':
      // Validate required parameters
      if (!params.adminId) {
        throw new Error('adminId is required for adminProfileImage');
      }
      if (!params.fileName) {
        throw new Error('fileName is required for adminProfileImage');
      }

      return `aai-users/${params.userId}/administrators/${params.adminId}/profile-img/${params.fileName}`;

    case 'voiceActorProfileImage':
      // Validate required parameters
      if (!params.actorId) {
        throw new Error('actorId is required for voiceActorProfileImage');
      }
      if (!params.fileName) {
        throw new Error('fileName is required for voiceActorProfileImage');
      }

      return `aai-users/${params.userId}/aai-actors/${params.actorId}/profile-img/${params.fileName}`;

    case 'bookCreatorProfileImage':
      // Validate required parameters
      if (!params.creatorId) {
        throw new Error('creatorId is required for bookCreatorProfileImage');
      }
      if (!params.fileName) {
        throw new Error('fileName is required for bookCreatorProfileImage');
      }

      return `aai-users/${params.userId}/book-creator/${params.creatorId}/profile-img/${params.fileName}`;

    case 'literaryGroupProfileImage':
      // Validate required parameters
      if (!params.litGroupId) {
        throw new Error('litGroupId is required for literaryGroupProfileImage');
      }
      if (!params.fileName) {
        throw new Error('fileName is required for literaryGroupProfileImage');
      }

      return `aai-users/${params.userId}/lit-groups/${params.litGroupId}/profile-img/${params.fileName}`;

    case 'characterAvatarImage':
      // Validate required parameters
      if (!params.actorId) {
        throw new Error('actorId is required for characterAvatarImage');
      }
      if (!params.portfolioId) {
        throw new Error('portfolioId is required for characterAvatarImage');
      }
      if (!params.characterId) {
        throw new Error('characterId is required for characterAvatarImage');
      }
      if (!params.fileName) {
        throw new Error('fileName is required for characterAvatarImage');
      }

      return `aai-users/${params.userId}/aai-actors/${params.actorId}/act-portfolios/${params.portfolioId}/act-characters/${params.characterId}/avatar/${params.fileName}`;

    case 'variationAudio':
      // Validate required parameters
      if (!params.actorId) {
        throw new Error('actorId is required for variationAudio');
      }
      if (!params.portfolioId) {
        throw new Error('portfolioId is required for variationAudio');
      }
      if (!params.characterId) {
        throw new Error('characterId is required for variationAudio');
      }
      if (!params.variationId) {
        throw new Error('variationId is required for variationAudio');
      }
      if (!params.fileName) {
        throw new Error('fileName is required for variationAudio');
      }

      return `aai-users/${params.userId}/aai-actors/${params.actorId}/act-portfolios/${params.portfolioId}/act-characters/${params.characterId}/act-variations/${params.variationId}/${params.fileName}`;

    default:
      throw new Error(`Unknown resource type: ${resourceType}`);
  }
};

/**
 * Generate a full S3 URL for a resource
 * @param {string} resourceType - The type of resource
 * @param {Object} params - The parameters for the path
 * @returns {string} The generated S3 URL
 */
export const generateS3Url = (resourceType, params) => {
  const path = generateS3Path(resourceType, params);
  return `${s3HttpsBase()}${path}`; // always-https base — never emit an s3:// asset URL
};

/**
 * Extract parameters from an S3 path
 * @param {string} path - The S3 path
 * @returns {Object} The extracted parameters
 */
export const extractS3PathParams = (path) => {
  // Split the path into parts
  const parts = path.split('/').filter(part => part.trim() !== '');

  // Initialize parameters object
  const params = {
    fileName: parts[parts.length - 1] // Last part is always the file name
  };

  // Extract userId if present
  const aaiUsersIndex = parts.indexOf('aai-users');
  if (aaiUsersIndex >= 0 && aaiUsersIndex + 1 < parts.length) {
    const potentialUserId = parts[aaiUsersIndex + 1];
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(potentialUserId)) {
      params.userId = potentialUserId;
    }
  }

  // Extract adminId if present
  const administratorsIndex = parts.indexOf('administrators');
  if (administratorsIndex >= 0 && administratorsIndex + 1 < parts.length) {
    const potentialAdminId = parts[administratorsIndex + 1];
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(potentialAdminId)) {
      params.adminId = potentialAdminId;
    }
  }

  // Extract actorId if present
  const aaiActorsIndex = parts.indexOf('aai-actors');
  if (aaiActorsIndex >= 0 && aaiActorsIndex + 1 < parts.length) {
    const potentialActorId = parts[aaiActorsIndex + 1];
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(potentialActorId)) {
      params.actorId = potentialActorId;
    }
  }

  // Extract creatorId if present
  const bookCreatorIndex = parts.indexOf('book-creator');
  if (bookCreatorIndex >= 0 && bookCreatorIndex + 1 < parts.length) {
    const potentialCreatorId = parts[bookCreatorIndex + 1];
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(potentialCreatorId)) {
      params.creatorId = potentialCreatorId;
    }
  }

  // Extract litGroupId if present
  const litGroupsIndex = parts.indexOf('lit-groups');
  if (litGroupsIndex >= 0 && litGroupsIndex + 1 < parts.length) {
    const potentialLitGroupId = parts[litGroupsIndex + 1];
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(potentialLitGroupId)) {
      params.litGroupId = potentialLitGroupId;
    }
  }

  // Extract portfolioId if present
  const actPortfoliosIndex = parts.indexOf('act-portfolios');
  if (actPortfoliosIndex >= 0 && actPortfoliosIndex + 1 < parts.length) {
    const potentialPortfolioId = parts[actPortfoliosIndex + 1];
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(potentialPortfolioId)) {
      params.portfolioId = potentialPortfolioId;
    }
  }

  // Extract characterId if present
  const actCharactersIndex = parts.indexOf('act-characters');
  if (actCharactersIndex >= 0 && actCharactersIndex + 1 < parts.length) {
    const potentialCharacterId = parts[actCharactersIndex + 1];
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(potentialCharacterId)) {
      params.characterId = potentialCharacterId;
    }
  }

  // Extract variationId if present
  const actVariationsIndex = parts.indexOf('act-variations');
  if (actVariationsIndex >= 0 && actVariationsIndex + 1 < parts.length) {
    const potentialVariationId = parts[actVariationsIndex + 1];
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(potentialVariationId)) {
      params.variationId = potentialVariationId;
    }
  }

  // Determine resource type
  if (parts.includes('administrators') && parts.includes('profile-img')) {
    params.resourceType = 'adminProfileImage';
  } else if (parts.includes('aai-actors') && parts.includes('profile-img')) {
    params.resourceType = 'voiceActorProfileImage';
  } else if (parts.includes('book-creator') && parts.includes('profile-img')) {
    params.resourceType = 'bookCreatorProfileImage';
  } else if (parts.includes('lit-groups') && parts.includes('profile-img')) {
    params.resourceType = 'literaryGroupProfileImage';
  } else if (parts.includes('act-characters') && parts.includes('avatar')) {
    params.resourceType = 'characterAvatarImage';
  } else if (parts.includes('act-variations')) {
    params.resourceType = 'variationAudio';
  }

  return params;
};

/**
 * Determine if a path is an S3 path
 * @param {string} path - The path to check
 * @returns {boolean} Whether the path is an S3 path
 */
export const isS3Path = (path) => {
  if (!path || typeof path !== 'string') {
    return false;
  }

  return path.includes('s3.') ||
    path.includes('amazonaws.com') ||
    path.startsWith('https://aai-api') ||
    path.includes('aai-users/');
};

/**
 * Check if a URL is a properly signed S3 URL
 * @param {string} url - The URL to check
 * @returns {boolean} Whether the URL is a properly signed S3 URL
 */
export const isProperlySignedS3Url = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  return url.includes('X-Amz-Signature=') &&
    url.includes('X-Amz-Algorithm=');
};

/**
 * Extract S3 path from a URL
 * @param {string} url - The URL to extract the path from
 * @returns {string} The extracted path
 */
export const extractS3PathFromUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return '';
  }

  try {
    const parsedUrl = new URL(url);
    let path = parsedUrl.pathname;

    // Remove leading slash if present
    if (path.startsWith('/')) {
      path = path.substring(1);
    }

    return path;
  } catch (error) {
    console.error('Error extracting S3 path from URL:', error);
    return url; // Return the original URL as fallback
  }
};

/**
 * Get a signed URL for an S3 path
 * @param {string} url - The URL or path
 * @param {string} userId - The user ID
 * @returns {Promise<string>} The signed URL
 */
export const getSignedUrlForS3Path = async (url, userId) => {
  // If it's already properly signed, return it
  if (isProperlySignedS3Url(url)) {
    return url;
  }

  // ⭐ ALWAYS THE RAW ENDPOINT. This function is handed a key that ALREADY EXISTS — a stored
  // asset URL read back from the database — so the only correct thing to do is sign that exact
  // key. `/get-signed-url-by-raw-path` does precisely that: parse, take the pathname as the key,
  // sign it.
  //
  // ⛔ The path endpoint was the wrong tool and used to be reached two ways: as a fallback when
  // the raw call failed, and for any input that was not already a full URL. It does not look a
  // path up — it MATCHES the path against the registry's required segments and then REBUILDS the
  // key from the winning template. Round-tripping a known-good key through that can only lose
  // fidelity: pick a different definition and you sign a URL for an object that does not exist,
  // which surfaces as a silently broken image. Matching is for callers who know a resourceType,
  // and this caller never does.
  //
  // A bare key is turned into a URL here rather than sent to the matcher. s3HttpsBase() is always
  // https even if the bucket env is set to `s3://…`, which is what stopped `s3://`-prefixed junk
  // being persisted as asset URLs in the first place.
  const absoluteUrl = url.startsWith('http')
    ? url
    : `${s3HttpsBase()}${normalizeS3Key(url)}`;

  try {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const response = await axios.post(config.API_GET_SIGNED_URL_BY_RAW_PATH, {
      userId,
      url: absoluteUrl
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      }
    });

    return response.data.signedUrl;
  } catch (error) {
    console.error('Error getting signed URL:', error);
    return url; // Fall back to the original URL — never to a guessed key
  }
};

/**
 * Make the actual signed URL request (internal function)
 * @param {string} userId - The user ID
 * @param {string} path - The path to get a signed URL for
 * @param {string} resourceType - The type of resource (path definition name)
 * @param {string} [fileName] - Optional explicit filename to use
 * @returns {Promise<string>} The signed URL
 */
const makeSignedUrlRequest = async (userId, path, resourceType = null, fileName = null) => {
  // Check for suspicious patterns in the path
  if (path.includes('userId') || path.includes('adminId') || path.includes('actorId')) {
    console.warn('WARNING: Path contains template placeholders instead of actual values:', path);
    console.warn('This may indicate a path construction issue!');
  }

  // Check for quote characters in the path
  if (path.includes('"') || path.includes("'")) {
    console.warn('WARNING: Path contains quote characters:', path);
    console.warn('This may indicate a string escaping issue!');
  }

  // Validate path structure
  const pathParts = path.split('/').filter(part => part.trim() !== '');
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const uuids = pathParts.filter(part => uuidPattern.test(part));

  // Get the authentication token from localStorage
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');

  // Prepare request data
  const requestData = {
    userId,
    path,
    resourceType, // Include the resourceType in the request
    fileName      // Include the fileName in the request if provided
  };
  console.log('Request data being sent to server:', JSON.stringify(requestData, null, 2));

  // Make API request to get signed URL using axios with full URL from config
  console.log('Sending request to:', config.API_GET_SIGNED_URL_BY_PATH);
  const response = await axios.post(config.API_GET_SIGNED_URL_BY_PATH, requestData, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    }
  });

  const data = response.data;
  console.log('Got signed URL:', data.signedUrl);

  return data.signedUrl;
};

/**
 * Get a signed URL for a path (with caching)
 * @param {string} userId - The user ID
 * @param {string} path - The path to get a signed URL for
 * @param {string} resourceType - The type of resource (path definition name)
 * @param {string} [fileName] - Optional explicit filename to use
 * @returns {Promise<string>} The signed URL
 */
export const getSignedUrlForPath = async (userId, path, resourceType = null, fileName = null) => {
  try {
    console.log('[SIGNED_URL_CACHE] Getting signed URL for path:', path);
    console.log('[SIGNED_URL_CACHE] Path type:', typeof path);
    console.log('[SIGNED_URL_CACHE] Resource type:', resourceType);

    // Create cache key
    const cacheKey = `${userId}:${path}:${fileName || ''}`;
    const now = Date.now();

    // Layer 1: Check for in-flight requests (deduplication)
    if (requestCache.has(cacheKey)) {
      const entry = requestCache.get(cacheKey);
      if (now <= entry.expires) {
        console.log('[SIGNED_URL_CACHE] Request in-flight, waiting for existing request');
        return await entry.promise;
      } else {
        // Expired entry, remove it
        requestCache.delete(cacheKey);
      }
    }

    // Layer 2: Check cached signed URLs
    const cached = urlCache.get(cacheKey);
    if (cached && now < cached.expires) {
      console.log('[SIGNED_URL_CACHE] Cache hit! Returning cached URL');
      return cached.url;
    } else if (cached) {
      // Expired entry, remove it
      console.log('[SIGNED_URL_CACHE] Cache entry expired, removing');
      urlCache.delete(cacheKey);
    }

    // Cache miss - make the request
    console.log('[SIGNED_URL_CACHE] Cache miss, making API request');

    // Create promise and store in request cache for deduplication
    const promise = makeSignedUrlRequest(userId, path, resourceType, fileName);
    requestCache.set(cacheKey, {
      promise,
      expires: now + REQUEST_CACHE_TTL
    });

    try {
      const url = await promise;

      // Store result in URL cache
      urlCache.set(cacheKey, {
        url,
        expires: now + URL_CACHE_TTL
      });

      console.log('[SIGNED_URL_CACHE] URL cached successfully');
      return url;
    } catch (error) {
      console.error('[SIGNED_URL_CACHE] Error getting signed URL:', error);
      throw error;
    } finally {
      // Always remove from request cache when done
      requestCache.delete(cacheKey);
    }
  } catch (error) {
    console.error('[SIGNED_URL_CACHE] Error in getSignedUrlForPath:', error);

    // Add more detailed error logging
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Server responded with error:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received from server:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error setting up request:', error.message);
    }

    throw error;
  }
};

/**
 * Get all path definitions from the server
 * @returns {Promise<Array>} The path definitions
 */
export const getPathDefinitions = async () => {
  try {
    console.log('Getting path definitions');

    // Get the authentication token from localStorage
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');

    // Make API request to get path definitions using axios with full URL from config
    const response = await axios.get(config.API_PATH_DEFINITIONS, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      }
    });

    const data = response.data;
    console.log(`Got ${data.length} path definitions`);

    return data;
  } catch (error) {
    console.error('Error getting path definitions:', error);
    throw error;
  }
};
